
import { supabase } from '@/integrations/supabase/client';
import { UserTechnique } from '@/types/technique';
import { TechniqueDifficulty } from '@/types/technique';
import type { Database } from '@/integrations/supabase/types';

type UserTechniqueRow = Database['public']['Tables']['user_techniques']['Row'] & {
  technique: Database['public']['Tables']['techniques']['Row'];
};

export async function getUserTechniques(userId: string): Promise<UserTechnique[]> {
  const { data, error } = await supabase
    .from('user_techniques')
    .select(`
      *,
      technique:techniques(*)
    `)
    .eq('user_id', userId);

  if (error) {
    console.error('Error fetching user techniques:', error);
    throw error;
  }

  // Transform the data to match our frontend types
  return data.map((item: UserTechniqueRow) => ({
    id: item.id,
    user_id: item.user_id,
    technique_id: item.technique_id,
    technique: {
      id: item.technique.id,
      name: item.technique.name,
      slug: item.technique.slug,
      description: item.technique.description,
      position_id: item.technique.position_id,
      technique_position: item.technique.technique_position,
      technique_type: item.technique.technique_type,
      difficulty: item.technique.difficulty as TechniqueDifficulty,
      image_url: item.technique.image_url,
      video_url: item.technique.video_url,
      key_points: item.technique.key_points,
      common_mistakes: item.technique.common_mistakes,
      category: item.technique.category,
      subcategory: item.technique.subcategory,
      is_user_created: item.technique.is_user_created,
      created_by_user_id: item.technique.created_by_user_id,
      is_private: item.technique.is_private,
      created_at: item.technique.created_at,
      updated_at: item.technique.updated_at
    },
    proficiency_level: item.proficiency_level || 1,
    notes: item.notes,
    is_favorite: item.is_favorite || false,
    last_practiced: item.last_practiced,
    created_at: item.created_at
  }));
}

// Function to get user's saved techniques (alias for getUserTechniques)
export async function getUserSavedTechniques(userId?: string): Promise<UserTechnique[]> {
  // Get the current user's ID if not provided
  if (!userId) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    userId = user.id;
  }
  
  return getUserTechniques(userId!);
}

// Function to check if a technique is saved by the current user
export async function checkIfTechniqueIsSaved(techniqueId: string): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  
  const { data, error } = await supabase
    .from('user_techniques')
    .select('id')
    .eq('user_id', user.id)
    .eq('technique_id', techniqueId)
    .maybeSingle();
  
  if (error) {
    console.error('Error checking if technique is saved:', error);
    return false;
  }
  
  return !!data;
}

// Function to save a technique to user's collection
export async function saveTechnique(techniqueId: string): Promise<UserTechnique | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');
  
  // Check if the technique is already saved
  const isSaved = await checkIfTechniqueIsSaved(techniqueId);
  if (isSaved) {
    console.log('Technique already saved');
    return null;
  }
  
  return createUserTechnique(user.id, techniqueId);
}

export async function createUserTechnique(userId: string, techniqueId: string): Promise<UserTechnique | null> {
  const { data, error } = await supabase
    .from('user_techniques')
    .insert([
      { user_id: userId, technique_id: techniqueId }
    ])
    .select(`
      *,
      technique:techniques(*)
    `)
    .single();

  if (error) {
    console.error('Error creating user technique:', error);
    throw error;
  }

  return {
    id: data.id,
    user_id: data.user_id,
    technique_id: data.technique_id,
    technique: {
      id: data.technique.id,
      name: data.technique.name,
      slug: data.technique.slug,
      description: data.technique.description,
      position_id: data.technique.position_id,
      technique_position: data.technique.technique_position,
      technique_type: data.technique.technique_type,
      difficulty: data.technique.difficulty as TechniqueDifficulty,
      image_url: data.technique.image_url,
      video_url: data.technique.video_url,
      key_points: data.technique.key_points,
      common_mistakes: data.technique.common_mistakes,
      category: data.technique.category,
      subcategory: data.technique.subcategory,
      is_user_created: data.technique.is_user_created,
      created_by_user_id: data.technique.created_by_user_id,
      is_private: data.technique.is_private,
      created_at: data.technique.created_at,
      updated_at: data.technique.updated_at
    },
    proficiency_level: data.proficiency_level || 1,
    notes: data.notes,
    is_favorite: data.is_favorite || false,
    last_practiced: data.last_practiced,
    created_at: data.created_at
  };
}

// Function to remove a technique from user's collection
export async function removeTechnique(techniqueId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');
  
  const { error } = await supabase
    .from('user_techniques')
    .delete()
    .eq('user_id', user.id)
    .eq('technique_id', techniqueId);
  
  if (error) {
    console.error('Error removing technique:', error);
    throw error;
  }
}

export async function deleteUserTechnique(id: string): Promise<void> {
  const { error } = await supabase
    .from('user_techniques')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting user technique:', error);
    throw error;
  }
}
